import type { BusinessType } from "@hellocashflow/domain";

import {
  VNextButton,
  VNextCard,
  VNextField,
  VNextInput,
  VNextPill,
} from "@/vnext/primitives";

interface BusinessTypesListCardProps {
  getActionDisabledReason?: (item: BusinessType) => string | undefined;
  editingId: string | null;
  editingLabel: string;
  emptyState: string;
  isBusy: boolean;
  items: BusinessType[];
  onAction: (id: string, label: string) => void;
  onCancelEdit: () => void;
  onEditingLabelChange: (value: string) => void;
  onSaveEdit: (id: string) => void;
  onStartEdit?: (id: string, label: string) => void;
  savingLabel?: string;
  title: string;
  tone: "default" | "warning";
}

const ACTION_LABEL_BY_TONE: Record<BusinessTypesListCardProps["tone"], string> =
  {
    default: "Restore",
    warning: "Archive",
  };

export function BusinessTypesListCard({
  getActionDisabledReason,
  editingId,
  editingLabel,
  emptyState,
  isBusy,
  items,
  onAction,
  onCancelEdit,
  onEditingLabelChange,
  onSaveEdit,
  onStartEdit,
  savingLabel = "Save",
  title,
  tone,
}: BusinessTypesListCardProps) {
  return (
    <VNextCard title={title}>
      {items.length === 0 ? (
        <div className="vnext-empty">{emptyState}</div>
      ) : (
        <div className="business-types-route__list">
          {items.map((item) => {
            const isEditing = editingId === item.id;
            const actionDisabledReason = getActionDisabledReason?.(item);
            const actionDisabledReasonId = `business-type-${item.id}-action-disabled-reason`;

            return (
              <article className="business-types-route__row" key={item.id}>
                <div className="business-types-route__row-main">
                  <div className="business-types-route__row-header">
                    <div>
                      {isEditing ? (
                        <VNextField label="Business type label">
                          <VNextInput
                            autoFocus
                            disabled={isBusy}
                            onChange={(event) =>
                              onEditingLabelChange(event.target.value)
                            }
                            value={editingLabel}
                          />
                        </VNextField>
                      ) : (
                        <h3 className="business-types-route__row-title">
                          {item.label}
                        </h3>
                      )}
                    </div>
                    <VNextPill
                      tone={tone === "warning" ? "success" : "default"}
                    >
                      {tone === "warning" ? "Active" : "Archived"}
                    </VNextPill>
                  </div>
                </div>

                <div className="business-types-route__row-actions">
                  {isEditing ? (
                    <>
                      <VNextButton
                        aria-label={`Save ${item.label} business type`}
                        disabled={isBusy}
                        onClick={() => onSaveEdit(item.id)}
                      >
                        {savingLabel}
                      </VNextButton>
                      <VNextButton
                        aria-label={`Cancel editing ${item.label} business type`}
                        disabled={isBusy}
                        onClick={onCancelEdit}
                        tone="quiet"
                      >
                        Cancel
                      </VNextButton>
                    </>
                  ) : (
                    <>
                      {onStartEdit ? (
                        <VNextButton
                          aria-label={`Edit ${item.label} business type`}
                          disabled={isBusy}
                          onClick={() => onStartEdit(item.id, item.label)}
                          tone="quiet"
                        >
                          Edit
                        </VNextButton>
                      ) : null}
                      <VNextButton
                        aria-label={`${ACTION_LABEL_BY_TONE[tone]} ${item.label} business type`}
                        aria-describedby={
                          actionDisabledReason
                            ? actionDisabledReasonId
                            : undefined
                        }
                        disabled={isBusy || Boolean(actionDisabledReason)}
                        onClick={() => onAction(item.id, item.label)}
                        tone={tone === "warning" ? "ghost" : "secondary"}
                      >
                        {ACTION_LABEL_BY_TONE[tone]}
                      </VNextButton>
                      {actionDisabledReason ? (
                        <small
                          className="business-types-route__action-disabled-reason"
                          id={actionDisabledReasonId}
                        >
                          {actionDisabledReason}
                        </small>
                      ) : null}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </VNextCard>
  );
}
